import React, { useState } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCuisineIcon } from '@/utils/iconMaps';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

interface CuisineSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
}

// Top 6 most common cuisines
const TOP_CUISINES = [
  { value: 'italian', label: 'Italian' },
  { value: 'indian', label: 'Indian' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'mexican', label: 'Mexican' },
  { value: 'thai', label: 'Thai' },
];

// All available cuisines
const ALL_CUISINES = [
  ...TOP_CUISINES,
  { value: 'american', label: 'American' },
  { value: 'french', label: 'French' },
  { value: 'greek', label: 'Greek' },
  { value: 'korean', label: 'Korean' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'middle_eastern', label: 'Middle Eastern' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'turkish', label: 'Turkish' },
  { value: 'vietnamese', label: 'Vietnamese' },
  { value: 'other', label: 'Other' },
];

export const CuisineSelector: React.FC<CuisineSelectorProps> = ({
  value,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredCuisines = ALL_CUISINES.filter((cuisine) =>
    cuisine.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (cuisineValue: string) => {
    if (value.includes(cuisineValue)) {
      onChange(value.filter((v) => v !== cuisineValue));
    } else {
      onChange([...value, cuisineValue]);
    }
  };

  return (
    <div className="space-y-3">
      {/* Top 6 Cuisines */}
      <div className="grid grid-cols-3 gap-2">
        {TOP_CUISINES.map((cuisine) => {
          const isSelected = value.includes(cuisine.value);
          return (
            <button
              key={cuisine.value}
              type="button"
              onClick={() => handleSelect(cuisine.value)}
              className={cn(
                'flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border-2 transition-all duration-200',
                'hover:scale-105 active:scale-95',
                !isSelected && 'bg-muted/50 border-border text-muted-foreground hover:border-border/80',
                isSelected && 'bg-[#cfef00]/20 border-[#cfef00] text-foreground ring-1 ring-[#cfef00]'
              )}
            >
              <span className="text-2xl">{getCuisineIcon(cuisine.value)}</span>
              <span className={cn(
                'text-xs font-medium',
                isSelected && 'font-semibold'
              )}>
                {cuisine.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* More Button */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-border/80 hover:text-foreground transition-colors"
          >
            <span className="text-sm font-medium">More Cuisines</span>
            <ChevronDown className="h-4 w-4" />
          </button>
        </SheetTrigger>

        <SheetContent side="bottom" className="h-[80vh] flex flex-col p-0 [&>button]:hidden">
          <div className="flex-shrink-0 px-4 pt-4 pb-4 border-b border-border flex items-center justify-between">
            <div className="w-16" />
            <h2 className="text-lg font-semibold text-foreground">Select Cuisine</h2>
            <div className="w-16 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                Close
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden px-4 pt-4 space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search cuisines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Cuisine List */}
            <ScrollArea className="h-[calc(80vh-160px)]">
              <div className="grid grid-cols-2 gap-2 pr-4">
                {filteredCuisines.map((cuisine) => {
                  const isSelected = value.includes(cuisine.value);
                  return (
                    <button
                      key={cuisine.value}
                      type="button"
                      onClick={() => handleSelect(cuisine.value)}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
                        'hover:scale-105 active:scale-95',
                        !isSelected && 'bg-muted/50 border-border text-muted-foreground hover:border-border/80',
                        isSelected && 'bg-[#cfef00]/20 border-[#cfef00] text-foreground ring-1 ring-[#cfef00]'
                      )}
                    >
                      <span className="text-xl">{getCuisineIcon(cuisine.value)}</span>
                      <span className={cn(
                        'text-sm font-medium',
                        isSelected && 'font-semibold'
                      )}>
                        {cuisine.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
